/**
 * Zone API wrapper
 *
 * - `ensureZoneObjectType(projectId)`: idempotent fetch-or-create of the
 *   project-scoped Zone ObjectType (single instance per project).
 * - `saveZoneMapping(projectId, floorId, payload)`: persist one Zone polygon
 *   as a LayerMapping with `entity_type='polygon'` and Zone properties.
 *
 * The Zone LayerMapping reuses the existing `/layer-type-mappings` endpoint.
 * Each Zone is addressed by a client-generated UUID in `entity_handle` and
 * a unique `layer_name` suffix (`ZONE-<uuid>`) so multiple zones coexist on
 * the same floor without colliding with the DXF-derived layer rows.
 */

import apiClient from './client';
import API_CONFIG from '../config/api.config';
import type { APIResponse } from './types';
import type {
  CreateObjectTypeRequest,
  EntityData,
  LayerMappingResponse,
  ObjectTypeResponse,
  Point,
} from './types/layerMapping';
import { objectTypeApi } from './services/layerMappingService';

// 한글 이름으로 통일 (사용자 요청). 기존 'Zone'/'존' 으로 생성된 record 도 isZoneObjectType 에서 인식.
export const ZONE_OBJECT_TYPE_NAME = '구역';
/** 마이그레이션 호환용 — 기존 이름도 zone 으로 인식 */
export const ZONE_OBJECT_TYPE_LEGACY_NAMES = ['Zone', 'zone', '존'];
export const ZONE_DEFAULT_COLOR = '#3A8EEE';
/**
 * Zone cell 의 data.layer 값. export 시 'zones' 버킷에 매핑되도록 plural
 * 소문자 'zones' 로 사용 (router/DB 와 동일 명명).
 *
 * 기존 'ZONE' (uppercase) 으로 저장된 데이터도 routeLayerToCategory 의
 * `/^zones?$/i` 패턴이 'zone' 카테고리로 라우팅 → 자동 호환.
 */
export const ZONE_LAYER_NAME = 'zones';
export const ZONE_ENTITY_TYPE = 'polygon';
export const ZONE_PRIORITY = 50;

export interface ZoneProperties {
  kind: 'zone';
  closed: true;
  name: string;
  memo?: string;
  /** Stroke color (#hex). 사용자 지정 가능. */
  color?: string;
}

/**
 * Extended EntityData for Zone polygons. `properties` is serialized as JSON
 * and carried alongside existing EntityData fields.
 */
export interface ZoneEntityData extends EntityData {
  properties: ZoneProperties;
}

export interface SaveZoneMappingPayload {
  objectTypeId: number;
  entityHandle: string;
  points: Point[];
  properties: ZoneProperties;
}

/**
 * Detect whether an ObjectType represents the Zone type.
 * 매칭 우선순위:
 *   1) 새 한글명 '존' (ZONE_OBJECT_TYPE_NAME)
 *   2) 레거시 영문명 'Zone' / 'zone' — 기존 데이터 호환
 */
export function isZoneObjectType(type: ObjectTypeResponse): boolean {
  if (type.name === ZONE_OBJECT_TYPE_NAME) return true;
  return ZONE_OBJECT_TYPE_LEGACY_NAMES.includes(type.name);
}

interface ListObjectTypesEnvelope {
  // Backend returns either an array or an object with `types`. Covering both.
  data?: ObjectTypeResponse[] | { types?: ObjectTypeResponse[] };
}

function extractObjectTypes(
  response: APIResponse<unknown>
): ObjectTypeResponse[] {
  const data = (response as ListObjectTypesEnvelope).data;
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object' && Array.isArray(data.types)) {
    return data.types;
  }
  return [];
}

export const zoneApi = {
  /**
   * Fetch the Zone ObjectType for a project, creating it if absent.
   *
   * Race handling: if two concurrent callers both see "absent" and the create
   * request fails with a duplicate (backend enforces unique name per project),
   * the caller should retry. This wrapper performs a single retry on the list.
   */
  async ensureZoneObjectType(projectId: number): Promise<ObjectTypeResponse> {
    const listResp = await objectTypeApi.getList(projectId);
    const all = extractObjectTypes(listResp);
    // 새 한글명 우선
    const current = all.find((t) => t.name === ZONE_OBJECT_TYPE_NAME);
    if (current) return current;

    // 레거시 ('Zone'/'존' 등) 가 있으면 → 이름을 '구역' 으로 자동 rename.
    // (중복 zone 타입이 두 개 생기지 않도록. 같은 ObjectType 의 id 유지 → 기존 polygon 들의 type 참조도 안 깨짐)
    const legacy = all.find((t) => ZONE_OBJECT_TYPE_LEGACY_NAMES.includes(t.name));
    if (legacy) {
      try {
        const updateResp = await objectTypeApi.update(projectId, legacy.id, {
          name: ZONE_OBJECT_TYPE_NAME,
        });
        if (updateResp.data) return updateResp.data;
      } catch {
        // rename 실패 (충돌 등) — 그냥 legacy 그대로 사용. 사용자에게 시각적으로는 보이지만 동작은 zone.
      }
      return legacy;
    }

    const req: CreateObjectTypeRequest = {
      name: ZONE_OBJECT_TYPE_NAME,
      color: ZONE_DEFAULT_COLOR,
      priority: ZONE_PRIORITY,
      properties: [],
    };

    try {
      const createResp = await objectTypeApi.create(projectId, req);
      if (!createResp.data) {
        throw new Error('Zone ObjectType create returned no data');
      }
      return createResp.data;
    } catch (error: unknown) {
      // Concurrent create lost the race; re-read once.
      const retryResp = await objectTypeApi.getList(projectId);
      const afterRace = extractObjectTypes(retryResp).find(isZoneObjectType);
      if (afterRace) return afterRace;
      throw error;
    }
  },

  /**
   * Persist a Zone polygon. `layer_name` is suffixed with the client UUID so
   * each zone gets its own row under the same `ZONE` namespace.
   */
  async saveZoneMapping(
    projectId: number,
    floorId: string,
    payload: SaveZoneMappingPayload
  ): Promise<APIResponse<LayerMappingResponse>> {
    const entityData: ZoneEntityData = {
      layer: ZONE_LAYER_NAME,
      entity_type: ZONE_ENTITY_TYPE,
      entity_handle: payload.entityHandle,
      points: payload.points,
      properties: payload.properties,
    };

    const body = {
      mappings: [
        {
          layer_name: `${ZONE_LAYER_NAME}-${payload.entityHandle}`,
          object_type_id: payload.objectTypeId,
          entity_handle: payload.entityHandle,
          entity_type: ZONE_ENTITY_TYPE,
          entity_data: entityData,
        },
      ],
    };

    return apiClient.post<LayerMappingResponse>(
      API_CONFIG.ENDPOINTS.LAYER_MAPPING.LAYER_TYPE_MAPPINGS(projectId, floorId),
      body
    );
  },
};

export default zoneApi;
